import React, { useEffect, useState } from 'react';
import { Plus, Minus, Trash2 } from 'lucide-react';
import {
  Dialog, DialogContent,
  DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { adminService } from '../../../services/adminService';
import { SuggestedQtyChip } from '../../../components/insights/SuggestedQtyChip';
import { CapChip } from '../../../components/insights/CapChip';
import { OverCapPanel } from '../../../components/insights/OverCapPanel';

const TIME_WINDOWS = [
  '7:00 AM - 9:00 AM',
  '9:00 AM - 11:00 AM',
  '11:00 AM - 1:00 PM',
  '1:00 PM - 3:00 PM',
  '3:00 PM - 5:00 PM',
];

/**
 * Self-contained "Create New Order" dialog. All state lives in the parent
 * (Orders.jsx); this component is a pure render layer.
 */
const NewOrderDialog = ({
  open,
  onOpenChange,
  // selections
  clients,
  newOrderClient,
  newOrderDate,
  newOrderTime,
  newOrderAddress,
  newOrderPO,
  newOrderNotes,
  newOrderItems,
  newOrderProducts,
  newOrderTotal,
  clientAddresses,
  loadingProducts,
  creatingOrder,
  // setters & handlers
  onClientChange,
  setNewOrderDate,
  setNewOrderTime,
  setNewOrderAddress,
  setNewOrderPO,
  setNewOrderNotes,
  onAddItem,
  onQtyChange,
  onPriceChange,
  onRemoveItem,
  onCreateOrder,
  // over-ordering
  capMap = {},
  overCapLines = [],
  capGatingActive = false,
  capReasons,
  capReason,
  setCapReason,
  capNote,
  setCapNote,
  capReasonMissing = false,
}) => {
  const selectedClient = clients.find((c) => c.id === newOrderClient);

  // Suggested quantities for the chosen client + delivery date (best-effort).
  const [suggMap, setSuggMap] = useState({});
  useEffect(() => {
    let ok = true;
    if (!open || !newOrderClient || !newOrderDate) { setSuggMap({}); return undefined; }
    adminService.getClientInsights(newOrderClient, newOrderDate)
      .then((res) => {
        if (!ok) return;
        const m = {};
        (res.suggestions || []).forEach((s) => { m[s.product_id] = s; });
        setSuggMap(m);
      })
      .catch(() => { if (ok) setSuggMap({}); });
    return () => { ok = false; };
  }, [open, newOrderClient, newOrderDate]);
  const tierClass = selectedClient?.has_guaranteed_returns
    ? 'bg-purple-100 text-purple-800'
    : (selectedClient?.discount_percent || 0) > 15
      ? 'bg-blue-100 text-blue-800'
      : 'bg-amber-100 text-amber-800';

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex items-center gap-3 flex-wrap">
          <DialogTitle>Create New Order</DialogTitle>
          {selectedClient?.client_type_name && (
            <Badge
              className={`text-xs ${tierClass}`}
              data-testid="new-order-pricing-tier-badge"
            >
              {selectedClient.client_type_name}
              {selectedClient.discount_percent > 0 && ` (${selectedClient.discount_percent}% off)`}
            </Badge>
          )}
        </div>
        <DialogDescription className="sr-only">Create New Order dialog. Use the form below to continue.</DialogDescription>
      </DialogHeader>
      <div className="space-y-5">
        {/* Client Selection */}
        <div>
          <Label>Client *</Label>
          <Select value={newOrderClient} onValueChange={onClientChange}>
            <SelectTrigger data-testid="new-order-client-select">
              <SelectValue placeholder="Select a client..." />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Delivery Date & Time */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Delivery Date *</Label>
            <Input
              type="date"
              value={newOrderDate}
              onChange={(e) => setNewOrderDate(e.target.value)}
              data-testid="new-order-date"
            />
          </div>
          <div>
            <Label>Delivery Time</Label>
            <Select value={newOrderTime} onValueChange={setNewOrderTime}>
              <SelectTrigger data-testid="new-order-time">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_WINDOWS.map((w) => (
                  <SelectItem key={w} value={w}>{w}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Delivery Address */}
        <div>
          <Label>Delivery Address</Label>
          {clientAddresses.length > 1 ? (
            <Select value={newOrderAddress} onValueChange={setNewOrderAddress}>
              <SelectTrigger data-testid="new-order-address">
                <SelectValue placeholder="Select delivery address..." />
              </SelectTrigger>
              <SelectContent>
                {clientAddresses.map((addr) => (
                  <SelectItem key={addr.value} value={addr.value}>
                    {addr.label}: {addr.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={newOrderAddress}
              onChange={(e) => setNewOrderAddress(e.target.value)}
              placeholder="Delivery address"
              data-testid="new-order-address"
            />
          )}
        </div>

        {/* PO Number & Notes */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>PO Number</Label>
            <Input
              value={newOrderPO}
              onChange={(e) => setNewOrderPO(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div>
            <Label>Order Notes</Label>
            <Input
              value={newOrderNotes}
              onChange={(e) => setNewOrderNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>

        {/* Product Picker */}
        <div>
          <Label className="text-base font-semibold">Products</Label>
          {loadingProducts ? (
            <p className="text-sm text-gray-500 py-2">Loading products...</p>
          ) : newOrderProducts.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">No active products available.</p>
          ) : (
            <div className="border rounded-lg mt-2 max-h-48 overflow-y-auto">
              {newOrderProducts
                .filter((p) => !newOrderItems.find((i) => i.product_id === p.id))
                .map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 cursor-pointer"
                    onClick={() => onAddItem(product)}
                    data-testid={`add-product-${product.id}`}
                  >
                    <div>
                      <span className="font-medium text-sm">{product.name}</span>
                      <span className="text-xs text-gray-500 ml-2">({product.pack_size})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">${product.your_price.toFixed(2)}</span>
                      <Plus className="h-4 w-4 text-amber-600" />
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Selected Items */}
        {newOrderItems.length > 0 && (
          <div>
            <Label className="text-base font-semibold">Order Items</Label>
            <div className="border rounded-lg mt-2 divide-y">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                <div className="col-span-4">Product</div>
                <div className="col-span-2 text-center">Qty</div>
                <div className="col-span-2 text-center">Price</div>
                <div className="col-span-2 text-right">Subtotal</div>
                <div className="col-span-2"></div>
              </div>
              {newOrderItems.map((item) => (
                <div key={item.product_id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                  <div className="col-span-4">
                    <p className="text-sm font-medium">{item.product_name}</p>
                    <p className="text-xs text-gray-500">{item.pack_size}</p>
                    {(suggMap[item.product_id] || capMap[item.product_id]) && (
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {suggMap[item.product_id] && (
                          <span data-testid={`new-order-suggestion-${item.product_id}`}>
                            <SuggestedQtyChip
                              suggestion={suggMap[item.product_id]}
                              onApply={(q) => onQtyChange(item.product_id, q)}
                            />
                          </span>
                        )}
                        {capMap[item.product_id] && (
                          <CapChip
                            cap={capMap[item.product_id]}
                            orderedQty={item.quantity}
                            onUseCap={(q) => onQtyChange(item.product_id, q)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 flex items-center justify-center gap-1">
                    <button
                      className="p-0.5 rounded hover:bg-gray-100"
                      onClick={() => onQtyChange(item.product_id, item.quantity - 1)}
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => onQtyChange(item.product_id, e.target.value)}
                      className="w-14 h-7 text-center text-sm"
                      min={1}
                    />
                    <button
                      className="p-0.5 rounded hover:bg-gray-100"
                      onClick={() => onQtyChange(item.product_id, item.quantity + 1)}
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => onPriceChange(item.product_id, e.target.value)}
                      className="w-full h-7 text-center text-sm"
                    />
                  </div>
                  <div className="col-span-2 text-right text-sm font-medium">
                    ${item.subtotal.toFixed(2)}
                  </div>
                  <div className="col-span-2 text-right">
                    <button
                      onClick={() => onRemoveItem(item.product_id)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="px-3 py-2 bg-gray-50 flex justify-between items-center">
                <span className="text-sm font-semibold">Total</span>
                <span className="text-lg font-bold text-amber-700" data-testid="new-order-total">
                  ${newOrderTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Over-ordering: the same warning the client would see */}
        <OverCapPanel
          lines={overCapLines}
          gated={capGatingActive}
          reasons={capReasons}
          reasonCode={capReason}
          onReasonChange={setCapReason}
          note={capNote}
          onNoteChange={setCapNote}
          onSetQty={onQtyChange}
          audience="admin"
          reasonMissing={capReasonMissing}
        />

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-amber-600 hover:bg-amber-700"
            onClick={onCreateOrder}
            disabled={creatingOrder || !newOrderClient || newOrderItems.length === 0 || !newOrderDate || capReasonMissing}
            data-testid="submit-new-order-btn"
          >
            {creatingOrder ? 'Creating...' : 'Create Order'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export default NewOrderDialog;
